/**
 * UsersAdminPage
 *
 * This is the original admin "users dashboard" content, extracted from the prior
 * `AdminDashboard.js`. It is rendered inside the admin dashboard shell.
 */

import React, { useState } from "react";
import { typography, surfaces, textColors } from "../../design-system/tokens";
import { Select } from "../../design-system/components/inputs";
import { calculateDateRange, formatNumber } from "../utils/chartHelpers";
import { useOverviewStats, useTimeseriesStats, usePeriodStats } from "../hooks/useAdminStats";

// Components
import StatCard from "../components/StatCard";
import RefreshButton from "../components/RefreshButton";
import ChartControls from "../components/ChartControls";
import TimeSeriesChart from "../components/TimeSeriesChart";
import PeriodBarChart from "../components/PeriodBarChart";

const statSeparatorClasses = `${textColors.primary} font-bold`;

const StatTitleTwoLine = ({ label }) => {
  return (
    <>
      <span className="block">{label}</span>
      <span className="block text-xs font-normal mt-1">
        <span className="text-green-400">New Today</span>
        <span className={textColors.primary}> {" / "} </span>
        <span className="text-blue-400">New this Week</span>
        <span className={textColors.primary}> {" / "} </span>
        <span className={textColors.primary}>Total</span>
      </span>
    </>
  );
};

const StatValueWithDeltas = ({ total = 0, today = 0, last7 = 0 }) => {
  return (
    <span className="inline-flex items-baseline flex-wrap">
      <span className="text-green-400 font-bold">{formatNumber(today)}</span>
      <span className={`${statSeparatorClasses} mx-2`}>/</span>
      <span className="text-blue-400 font-bold">{formatNumber(last7)}</span>
      <span className={`${statSeparatorClasses} mx-2`}>/</span>
      <span className={statSeparatorClasses}>{formatNumber(total)}</span>
    </span>
  );
};

const UsersOverviewPanel = () => {
  // Timezone state - default to NY time
  const [timezone, setTimezone] = useState("America/New_York");

  // Date range state - default to last 30 days
  const [datePreset, setDatePreset] = useState("last30days");
  const defaultRange = calculateDateRange("last30days");
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);

  // Time-series chart controls
  const [scale, setScale] = useState("linear"); // 'linear' or 'log'
  const [showUsers, setShowUsers] = useState(true);
  const [showContainers, setShowContainers] = useState(true);

  // Bar chart controls
  const [granularity, setGranularity] = useState("day"); // 'day', 'week', or 'month'

  // Timezone options
  const timezoneOptions = [
    { value: "America/New_York", label: "New York, USA" },
    { value: "America/Sao_Paulo", label: "São Paulo, Brazil" },
    { value: "UTC", label: "London, UK" },
    { value: "Asia/Kolkata", label: "Mumbai, India" },
  ];

  // Fetch data using hooks - pass timezone to overview stats only
  const overviewQuery = useOverviewStats(timezone);
  const timeseriesQuery = useTimeseriesStats(startDate, endDate, true);
  const usersQuery = usePeriodStats("users", granularity, startDate, endDate, true);
  const containersQuery = usePeriodStats("containers", granularity, startDate, endDate, true);
  const websitesQuery = usePeriodStats("websites", granularity, startDate, endDate, true);

  // Refresh all data
  const handleRefresh = () => {
    overviewQuery.refetch();
    timeseriesQuery.refetch();
    usersQuery.refetch();
    containersQuery.refetch();
    websitesQuery.refetch();
  };

  // Check if any query is refreshing
  const isRefreshing =
    overviewQuery.isFetching ||
    timeseriesQuery.isFetching ||
    usersQuery.isFetching ||
    containersQuery.isFetching ||
    websitesQuery.isFetching;

  // Handle date changes
  const handleStartDateChange = (newStartDate) => {
    setStartDate(newStartDate);
    setDatePreset(""); // Clear preset when manually changing dates
  };

  const handleEndDateChange = (newEndDate) => {
    setEndDate(newEndDate);
    setDatePreset(""); // Clear preset when manually changing dates
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className={`${typography.h2} ${textColors.primary}`}>Dashboard Overview</h1>
        <div className="flex items-center gap-3">
          <Select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            options={timezoneOptions}
            size="md"
            fullWidth={false}
            className="min-w-[120px]"
            aria-label="Select timezone for dashboard statistics"
          />
          <RefreshButton onRefresh={handleRefresh} isRefreshing={isRefreshing} />
        </div>
      </div>

      {/* Overview Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title={<StatTitleTwoLine label="Users" />}
          value={
            <StatValueWithDeltas
              total={overviewQuery.data?.total_users || 0}
              today={overviewQuery.data?.new_users_today || 0}
              last7={overviewQuery.data?.new_users_last_7_days || 0}
            />
          }
          subtitle="Registered accounts"
        />
        <StatCard
          title={<StatTitleTwoLine label="Containers" />}
          value={
            <StatValueWithDeltas
              total={overviewQuery.data?.total_containers || 0}
              today={overviewQuery.data?.new_containers_today || 0}
              last7={overviewQuery.data?.new_containers_last_7_days || 0}
            />
          }
          subtitle="Website containers"
        />
        <StatCard
          title={<StatTitleTwoLine label="Websites" />}
          value={
            <StatValueWithDeltas
              total={overviewQuery.data?.total_websites || 0}
              today={overviewQuery.data?.new_websites_today || 0}
              last7={overviewQuery.data?.new_websites_last_7_days || 0}
            />
          }
          subtitle="All versions"
        />
        <StatCard
          title={<StatTitleTwoLine label="Published" />}
          value={
            <StatValueWithDeltas
              total={overviewQuery.data?.published_websites || 0}
              today={overviewQuery.data?.new_published_today || 0}
              last7={overviewQuery.data?.new_published_last_7_days || 0}
            />
          }
          subtitle="Live websites"
        />
      </div>

      {/* Time-Series Chart Section */}
      <div className="mb-8">
        <ChartControls
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onPresetChange={setDatePreset}
          scale={scale}
          onScaleChange={setScale}
          showScaleToggle={true}
          showUsers={showUsers}
          showContainers={showContainers}
          onToggleUsers={() => setShowUsers(!showUsers)}
          onToggleContainers={() => setShowContainers(!showContainers)}
          showLineToggles={true}
        />

        <TimeSeriesChart
          data={timeseriesQuery.data?.data || []}
          scale={scale}
          showUsers={showUsers}
          showContainers={showContainers}
          loading={timeseriesQuery.isLoading}
          error={timeseriesQuery.error}
        />
      </div>

      {/* Period Bar Charts Section */}
      <div className="mb-6">
        <ChartControls
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onPresetChange={setDatePreset}
          granularity={granularity}
          onGranularityChange={setGranularity}
          showGranularitySelect={true}
        />
      </div>

      {/* Bar Charts Grid */}
      <div className="grid grid-cols-1 gap-6">
        <PeriodBarChart
          title="New Users per Period"
          data={usersQuery.data?.data || []}
          dataKey="count"
          barColor="#3b82f6"
          loading={usersQuery.isLoading}
          error={usersQuery.error}
          yAxisLabel="Users"
          granularity={granularity}
          startDate={startDate}
          endDate={endDate}
        />

        <PeriodBarChart
          title="New Containers per Period"
          data={containersQuery.data?.data || []}
          dataKey="count"
          barColor="#10b981"
          loading={containersQuery.isLoading}
          error={containersQuery.error}
          yAxisLabel="Containers"
          granularity={granularity}
          startDate={startDate}
          endDate={endDate}
        />

        <PeriodBarChart
          title="New Websites per Period"
          data={websitesQuery.data?.data || []}
          dataKey="count"
          barColor="#f59e0b"
          loading={websitesQuery.isLoading}
          error={websitesQuery.error}
          yAxisLabel="Websites"
          granularity={granularity}
          startDate={startDate}
          endDate={endDate}
        />
      </div>
    </div>
  );
};

const UsersAdminPage = () => {
  return (
    <div className={`${surfaces.modernApp}`}>
      <main className={`p-4 sm:p-6 ${surfaces.modernApp}`}>
        <UsersOverviewPanel />
      </main>
    </div>
  );
};

export default UsersAdminPage;


